import {
  Grid,
  TextField,
  Button,
  Box,
  CircularProgress,
  Autocomplete,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import dayjs from "dayjs";

import { getLawyers, getLawyerSessionStatus } from "../../services/LawyerService";
import { getGeneratePaymentOrders } from "../../services/PaymentOrderService";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import OnlineQueryTooltip, {
  useOnlineQueryGate,
} from "../General/OnlineQueryGate";
import { useToast } from "../../context/ToastContext";
import LawyerSessionRequiredDialog from "../General/Dialog/LawyerSessionRequiredDialog";

// Un abogado por vez: si el servidor no tiene su sesión guardada, se pide
// pegarla ANTES de disparar nada (ver Diagnóstico Turnstile).
export default function GeneratePaymentOrderByLawyer() {
  const { showToast } = useToast();
  const { online } = useOnlineQueryGate();
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(false);

  const [lawyers, setLawyers] = useState([]);
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [sessionRequiredLawyer, setSessionRequiredLawyer] = useState(null);
  const [lastSent, setLastSent] = useState(null);

  const fetchLawyers = async () => {
    setLoading(true);
    try {
      const res = await getLawyers();
      setLawyers(res?.data?.data || []);
    } catch {
      showToast("Error al cargar los abogados.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLawyers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchGeneration = async (lawyer) => {
    setLoading(true);
    try {
      const payload = {
        lawyers: [lawyer.id],
        payloadDate: {
          dateFrom: startDate.format("YYYY-MM-DD"),
          dateTo: endDate.format("YYYY-MM-DD"),
        },
      };

      const response = await getGeneratePaymentOrders(payload);

      showToast(
        response.data.message || "Órdenes de pago generadas correctamente.",
        "success"
      );
      setLastSent(lawyer);
      setSelectedLawyer(null);
      setStartDate(dayjs());
      setEndDate(dayjs());
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        showToast(error.response.data?.message || "Ocurrió un error.", "error");
      } else {
        console.error(error);
        showToast("Ocurrió un error inesperado.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedLawyer) {
      showToast("Debe seleccionar un abogado.", "info");
      return;
    }

    if (startDate && endDate && startDate.isAfter(endDate)) {
      showToast("La fecha de inicio no puede ser posterior a la fecha de fin.", "info");
      return;
    }

    if (!startDate || !endDate) {
      showToast("Debe seleccionar el rango de fechas.", "info");
      return;
    }

    setCheckingSession(true);
    try {
      const res = await getLawyerSessionStatus(selectedLawyer.id);
      if (!res.data?.data?.cached) {
        setSessionRequiredLawyer(selectedLawyer);
        return;
      }
    } catch {
      // Si falla la consulta de estado, se sigue igual.
    } finally {
      setCheckingSession(false);
    }

    await dispatchGeneration(selectedLawyer);
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>
        <LawyerSessionRequiredDialog
          open={Boolean(sessionRequiredLawyer)}
          onClose={() => setSessionRequiredLawyer(null)}
          lawyer={sessionRequiredLawyer}
          onCaptured={(lawyer) => dispatchGeneration(lawyer)}
        />

        <GlassCard>
          <CardHeader
            icon={<AssignmentIcon />}
            title="Generar Ordenes de Pagos"
            description="Generar órdenes de pago para un abogado. Elegí el abogado y el rango de fechas para iniciar el proceso."
          />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <Autocomplete
                  options={lawyers}
                  getOptionLabel={(option) => `${option.name} ${option.lastname}`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={selectedLawyer}
                  onChange={(event, newValue) => setSelectedLawyer(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Abogado" placeholder="Seleccionar..." />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                <DatePicker
                  label="Desde"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                <DatePicker
                  label="Hasta"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              {lastSent && (
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, opacity: 0.75 }}>
                    <CheckCircleRoundedIcon fontSize="small" color="success" />
                    <Typography sx={{ fontSize: 13.5 }}>
                      {lastSent.name} {lastSent.lastname} en proceso — podés elegir otro abogado.
                    </Typography>
                  </Box>
                </Grid>
              )}

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <OnlineQueryTooltip show={!online} block>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    disabled={loading || checkingSession || !online}
                  >
                    {loading || checkingSession ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Generar Órdenes"
                    )}
                  </Button>
                </OnlineQueryTooltip>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>
    </Grid>
  );
}
